/**
 * Scout v0 — run detail (P17, §1.9 observability).
 *
 * One run: identity + status, the per-stage funnel scorecard (each stage is an
 * inspectable checkpoint — a bad batch must be traceable to the stage that
 * degraded it), and the criteria RECORDED with the run (the idiot-proof tenet:
 * what is shown is exactly what launched, rendered from the stored snapshot).
 * Polls while the run is still in flight.
 */

import { Link } from '@tanstack/react-router';
import { useResumeRun, useScoutRun } from '@/lib/scout-queries';
import { EmptyState, ErrorBanner, LoadingSpinner } from '@/components/ui';
import { getErrorMessage } from '@/lib/queries';
import { RunStatusBadge } from './RunStatusBadge';
import type { RunDetail } from '@/types/scout';

function formatTimestamp(iso: string | null): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StageFunnel({ stages }: { stages: RunDetail['stages'] }) {
  if (stages.length === 0) {
    return (
      <p className="text-sm text-muted italic">
        No stage scorecard recorded for this run yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted border-b border-border">
            <th className="py-2 pr-3 font-medium">#</th>
            <th className="py-2 pr-3 font-medium">Stage</th>
            <th className="py-2 pr-3 font-medium text-right">In</th>
            <th className="py-2 pr-3 font-medium text-right">Out</th>
            <th className="py-2 pr-3 font-medium text-right">Dropped</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((stage) => (
            <tr key={stage.stage} className="border-b border-border/50">
              <td className="py-2 pr-3 text-muted">{stage.order}</td>
              <td className="py-2 pr-3 text-text font-mono text-xs">{stage.stage}</td>
              <td className="py-2 pr-3 text-right text-muted">{stage.inputCount}</td>
              <td className="py-2 pr-3 text-right text-text">{stage.outputCount}</td>
              <td className={`py-2 pr-3 text-right ${stage.droppedCount > 0 ? 'text-warning' : 'text-muted'}`}>
                {stage.droppedCount}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Resume action (P26) — failed runs only. Wraps the P07 resume lane (the same
 * checkpoint replay as `pnpm scout:resume`): completed stages, including
 * stage-7 contact routing, are reused, never re-run — a resume never
 * re-spends provider credits.
 */
function ResumeAction({ runId }: { runId: string }) {
  const resume = useResumeRun(runId);
  return (
    <div className="space-y-2">
      {resume.isError && (
        <ErrorBanner message={getErrorMessage(resume.error)} />
      )}
      <button
        type="button"
        onClick={() => resume.mutate()}
        disabled={resume.isPending}
        className="btn-primary text-sm px-4 py-2"
      >
        {resume.isPending ? 'Resuming…' : 'Resume run'}
      </button>
      <p className="text-xs text-muted">
        Continues from the last completed checkpoint - stages that already ran
        (including contact discovery) are not re-run and spend nothing.
      </p>
    </div>
  );
}

/**
 * QA gate entry (P18, §1.2 stage 10) — completed runs only. Opens the human
 * QA queue for this run's stage-9 graded leads.
 */
function ReviewQaAction({ runId }: { runId: string }) {
  return (
    <div className="space-y-2">
      <Link
        to="/scout/qa/$runId"
        params={{ runId }}
        className="btn-primary text-sm px-4 py-2 inline-block"
      >
        Review QA
      </Link>
      <p className="text-xs text-muted">
        Grade this run's stage-9 leads in the human QA gate.
      </p>
    </div>
  );
}

export function RunDetailPanel({ runId }: { runId: string }) {
  const { data: run, isLoading, error, refetch } = useScoutRun(runId);

  if (error) {
    return (
      <section className="card">
        <ErrorBanner message={getErrorMessage(error)} retryable={error.retryable} onRetry={() => refetch()} />
      </section>
    );
  }

  if (isLoading || !run) {
    return (
      <section className="card">
        <LoadingSpinner className="py-6" />
      </section>
    );
  }

  return (
    <div className="space-y-6">
      {/* Identity + status */}
      <section className="card" aria-labelledby="run-heading">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 id="run-heading" className="text-lg font-semibold text-text">
              Run
            </h2>
            <p className="text-xs text-muted mt-0.5 font-mono">
              {run.runId} · {run.criteriaVersion || '(no criteria version)'}
            </p>
          </div>
          <RunStatusBadge status={run.status} />
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="border border-border rounded-md p-2.5">
            <dt className="text-xs text-muted">Launched</dt>
            <dd className="text-sm text-text">{formatTimestamp(run.createdAt)}</dd>
          </div>
          <div className="border border-border rounded-md p-2.5">
            <dt className="text-xs text-muted">Completed</dt>
            <dd className="text-sm text-text">{formatTimestamp(run.completedAt)}</dd>
          </div>
          <div className="border border-border rounded-md p-2.5">
            <dt className="text-xs text-muted">Last update</dt>
            <dd className="text-sm text-text">{formatTimestamp(run.updatedAt)}</dd>
          </div>
        </dl>
        {run.status === 'completed' && (
          <div className="mt-4 border-t border-border pt-4">
            <ReviewQaAction runId={runId} />
          </div>
        )}
        {run.status === 'failed' && (
          <div className="mt-4 border-t border-border pt-4">
            <ResumeAction runId={runId} />
          </div>
        )}
      </section>

      {/* Stage funnel (§1.9 — inspectable per-stage checkpoints) */}
      <section className="card" aria-labelledby="funnel-heading">
        <h3 id="funnel-heading" className="text-base font-semibold text-text mb-3">
          Stage funnel
        </h3>
        <StageFunnel stages={run.stages} />
      </section>

      {/* The criteria recorded with the run (idiot-proof tenet) */}
      <section className="card" aria-labelledby="criteria-heading">
        <h3 id="criteria-heading" className="text-base font-semibold text-text mb-3">
          Criteria recorded with this run
        </h3>
        {run.preview ? (
          <div className="space-y-3">
            <p className="text-xs text-muted font-mono break-all">boolean: {run.preview.generatedBoolean}</p>
            <pre className="text-xs text-muted bg-bg border border-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap">
              {run.preview.text}
            </pre>
          </div>
        ) : (
          <EmptyState
            title="No criteria snapshot"
            description="This run predates criteria recording (pre-P14) or was launched outside the composer."
          />
        )}
      </section>
    </div>
  );
}
